/**
 * Package Management Service
 * Canonical implementation for package registry operations
 */

import { buildRegistryUrl } from "../../config/services-config";
import type { HttpClient } from "../client";
import type { Package, PackageListParams, PaginatedResponse } from "../types";

export class PackagesService {
	private httpClient: HttpClient;

	constructor(httpClient: HttpClient) {
		this.httpClient = httpClient;
	}

	// ==================== Package Listing ====================

	async getPackages(
		tenantSlug: string,
		params: PackageListParams = {},
	): Promise<PaginatedResponse<Package>> {
		const queryParams = new URLSearchParams();

		if (params.ecosystem) queryParams.set("ecosystem", params.ecosystem);
		if (params.search) queryParams.set("search", params.search);
		if (params.visibility) queryParams.set("visibility", params.visibility);
		if (params.sortBy) queryParams.set("sortBy", params.sortBy);
		if (params.sortOrder) queryParams.set("sortOrder", params.sortOrder);
		if (params.page) queryParams.set("page", params.page.toString());
		if (params.limit) queryParams.set("limit", params.limit.toString());
		if (params.tags?.length) queryParams.set("tags", params.tags.join(","));

		const queryString = queryParams.toString();
		const path = queryString ? `/packages?${queryString}` : "/packages";
		const url = buildRegistryUrl(tenantSlug, path);

		const response = await this.httpClient.get<{
			packages: Package[];
			pagination: any;
		}>(url);

		return {
			data: response.packages,
			pagination: {
				...response.pagination,
				hasNextPage: response.pagination.page < response.pagination.totalPages,
				hasPreviousPage: response.pagination.page > 1,
			},
		};
	}

	// ==================== Package Details ====================

	async getPackage(
		tenantSlug: string,
		ecosystem: string,
		packageName: string,
	): Promise<Package> {
		const url = buildRegistryUrl(
			tenantSlug,
			`/admin/packages/${ecosystem}/${encodeURIComponent(packageName)}`,
		);
		const response = await this.httpClient.get<{
			package: Package;
			versions: any[];
			stats: any;
		}>(url);

		return {
			...response.package,
			versions: response.versions,
		};
	}

	async getPackageById(
		tenantSlug: string,
		packageId: string,
	): Promise<Package> {
		const url = buildRegistryUrl(tenantSlug, `/admin/packages/${packageId}`);
		const response = await this.httpClient.get<{
			package: Package;
			versions: any[];
			stats: any;
		}>(url);

		return {
			...response.package,
			versions: response.versions,
		};
	}

	// ==================== Package Management ====================

	async deletePackage(
		tenantSlug: string,
		ecosystem: string,
		packageName: string,
	): Promise<void> {
		const packageId = `${ecosystem}/${encodeURIComponent(packageName)}`;
		const url = buildRegistryUrl(tenantSlug, `/admin/packages/${packageId}`);
		await this.httpClient.delete<void>(url);
	}

	async deletePackageById(
		tenantSlug: string,
		packageId: string,
	): Promise<void> {
		const url = buildRegistryUrl(tenantSlug, `/admin/packages/${packageId}`);
		await this.httpClient.delete<void>(url);
	}

	async updatePackageSettings(
		tenantSlug: string,
		ecosystem: string,
		packageName: string,
		settings: {
			visibility?: "public" | "private";
			description?: string;
			keywords?: string[];
		},
	): Promise<Package> {
		const packageId = `${ecosystem}/${encodeURIComponent(packageName)}`;
		const url = buildRegistryUrl(tenantSlug, `/admin/packages/${packageId}`);
		return await this.httpClient.patch<Package>(url, settings);
	}

	// ==================== Package Statistics ====================

	async getPackageStats(
		tenantSlug: string,
		ecosystem: string,
		packageName: string,
		timeframe: string = "30d",
	): Promise<any> {
		const url = buildRegistryUrl(
			tenantSlug,
			`/analytics/${ecosystem}/${encodeURIComponent(packageName)}/stats?timeframe=${timeframe}`,
		);
		return await this.httpClient.get<any>(url);
	}

	async getDownloadStats(
		tenantSlug: string,
		ecosystem?: string,
		packageName?: string,
		timeframe: string = "7d",
	): Promise<any> {
		const params = new URLSearchParams({ timeframe });
		if (ecosystem) params.set("ecosystem", ecosystem);
		if (packageName) params.set("package", packageName);

		const url = buildRegistryUrl(
			tenantSlug,
			`/analytics/downloads/timeline?${params.toString()}`,
		);
		return await this.httpClient.get<any>(url);
	}

	// ==================== Search ====================

	async searchPackages(
		tenantSlug: string,
		query: string,
		ecosystem?: string,
		limit: number = 20,
	): Promise<Package[]> {
		const params = new URLSearchParams({
			search: query,
			limit: limit.toString(),
		});
		if (ecosystem) params.set("ecosystem", ecosystem);

		const url = buildRegistryUrl(tenantSlug, `/packages?${params.toString()}`);
		const response = await this.httpClient.get<{ packages: Package[] }>(url);

		return response.packages;
	}

	// ==================== Ecosystem Statistics ====================

	async getEcosystemStats(tenantSlug: string): Promise<Record<string, any>> {
		const url = buildRegistryUrl(tenantSlug, "/analytics/ecosystems");
		return await this.httpClient.get<Record<string, any>>(url);
	}

	async getPopularPackages(
		tenantSlug: string,
		ecosystem?: string,
		limit: number = 10,
	): Promise<Package[]> {
		const params = new URLSearchParams({ limit: limit.toString() });
		if (ecosystem) params.set("ecosystem", ecosystem);

		const url = buildRegistryUrl(
			tenantSlug,
			`/analytics/packages/popular?${params.toString()}`,
		);
		const response = await this.httpClient.get<{ packages: Package[] }>(url);

		return response.packages;
	}
}
